<script setup>
// Stripe設定
// https://dashboard.stripe.com/
// テスト用カード
// https://pay.jp/docs/testcard
// 参考
// https://arrown-blog.com/php-laravel-stripe-creditcard2/

import { inject, ref, reactive, watch, computed, onMounted } from 'vue';
import { Inertia } from '@inertiajs/inertia';
import axios from 'axios';
import Alert from '@util/BrowserAlert';
import ImageFrame from '@component/ImageFrame.vue';

const emit = defineEmits(['completed', 'canceled']);

const publishableKey = inject('stripePublicKey');
const secretKey = inject('stripeSecretKey');

let stripe = null;
const cardHolder = ref('');
const paymentEnd = ref('');
const showLoading = ref(false)

const errors = reactive({
    cardNumber: {
        message: '',
        completed: false,
    },
    cardCvc: {
        message: '',
        completed: false,
    },
    cardExpiry: {
        message: '',
        completed: false,
    },
});
let processing = false;
const close = ref();

const onCompleted = computed(() => {
    return errors.cardNumber.completed && errors.cardCvc.completed && errors.cardExpiry.completed;
});

const onClickCancel = () => {
    emit('canceled');
};

// 文字数チェック
watch(
    () => cardHolder.value,
    (value) => {
        if(Alert.charLimitText(value)){
            cardHolder.value = value.substring(0 ,Alert.TEXT_LIMIT_NUMBER);
        }
    }
);

onMounted(async () => {

    stripe = Stripe(publishableKey);
    stripe.api_key = secretKey;
    var elements = stripe.elements();

    // Element作成時に options から スタイルを調整できます.
    var style = {
        base: {
            fontSize: '24px',
            color: '#000',
            '::placeholder': {
                color: '#cdd6dd',
            },
        },
        complete: {
            color: '#000',
        },
        empty: {
            color: '#000',
        },
        invalid: {
            color: '#000',
        }
    };

    // card Element のインスタンス作成
    var cardNumber = elements.create('cardNumber', { style: style, showIcon: true, placeholder: 'カード番号' });
    var cardCvc = elements.create('cardCvc', { style: style, placeholder: '' });
    var cardExpiry = elements.create('cardExpiry', { style: style });

    // マウント
    cardNumber.mount('#card-number');
    cardCvc.mount('#card-cvc');
    cardExpiry.mount('#card-expiry');

    // 入力フォームのチェック
    cardNumber.addEventListener('change', (event) => {
        errors.cardNumber.completed = event.complete;
        errors.cardNumber.message = event?.error?'カード番号が正しくありません。' : null;
    });
    cardCvc.addEventListener('change', (event) => {
        errors.cardCvc.completed = event.complete;
        errors.cardCvc.message = event?.error?.message ?? null;
    });
    cardExpiry.addEventListener('change', (event) => {
        errors.cardExpiry.completed = event.complete;
        errors.cardExpiry.message = event?.error?'有効期限が過ぎています。' : null;
    });

    var form = document.getElementById('payment-form');
    form?.addEventListener('submit', async (event) => {
        // デフォルトのSubmit処理を停止させる
        event.preventDefault();

        stripe.createToken(cardNumber, { name: cardHolder.value }).then((result) => {
            if (result.error) {
                Alert.Error('クレジットカード登録に失敗しました。');
            } else {
                stripeTokenHolder(result.token);
            }
        });
    });
});

const mountElements = () => {
    var elements = stripe.elements();

    // Element作成時に options から スタイルを調整できます.
    var style = {
        base: {
            fontSize: '24px',
            color: '#000',
            '::placeholder': {
                color: '#cdd6dd',
            },
        },
        complete: {
            color: '#000',
        },
        empty: {
            color: '#000',
        },
        invalid: {
            color: '#000',
        }
    };

    // card Element のインスタンス作成
    var cardNumber = elements.create('cardNumber', { style: style, showIcon: true, placeholder: 'カード番号' });
    var cardCvc = elements.create('cardCvc', { style: style, placeholder: '' });
    var cardExpiry = elements.create('cardExpiry', { style: style });

    // マウント
    cardNumber.mount('#card-number');
    cardCvc.mount('#card-cvc');
    cardExpiry.mount('#card-expiry');

    // 入力フォームのチェック
    cardNumber.addEventListener('change', (event) => {
        errors.cardNumber.completed = event.complete;
        errors.cardNumber.message = event?.error?'カード番号が正しくありません。' : null;
    });
    cardCvc.addEventListener('change', (event) => {
        errors.cardCvc.completed = event.complete;
        errors.cardCvc.message = event?.error?.message ?? null;
    });
    cardExpiry.addEventListener('change', (event) => {
        errors.cardExpiry.completed = event.complete;
        errors.cardExpiry.message = event?.error?'有効期限が過ぎています。' : null;
    });

    return { cardNumber, cardCvc, cardExpiry };
}

const stripeTokenHolder = (token) => {
    // カード情報をトークン化してサーバーに送る
    if (!processing) {
        processing = true;
        showLoading.value = true;
        // const fm = InertiaUtil.getForm({ token: token.id, card_token: token.card.id });
        axios.post(route('payment.store'), { token: token.id, card_token: token.card.id })
            .then((response) => {
                if (response?.data?.errors){
                    //Alert.Error(response?.data?.errors);
                    swal({
                        title: "確認",
                        text: response?.data?.errors,
                        icon: "warning",
                        buttons: {
                            ok: {
                                text: "OK",
                                className: "timeout-ok-btn",
                            },
                        },
                    }).then((value) => {
                        if (value === "ok") {
                            window.location.reload();
                        }
                    });
                    processing = false;
                } else{
                    if (response?.data?.payment_confirm_url){
                        window.location.href = response.data.payment_confirm_url;
                        
                    }else{
                        paymentEnd.value.click();
                    }
                    setTimeout(() => {
                        showLoading.value = false;
                    }, 10000);
                }
                processing = false;
                
                
            })
            .catch(() => {
                Alert.Error('クレジットカード登録に失敗しました。');
                processing = false;
                window.location.reload();
            });
    }
}
</script>
<template>
    <!--modal START-->
    <form id="payment-form" method="post">
        <div id="payment-modal" class="modal type3 js-modal">
            <div class="modal-bg bt_modal_close" @click="onClickCancel"></div>
            <div class="modal-content">
                <div class="close_bt bt_modal_close" @click="onClickCancel">
                    <image-frame src="images/common/i_bt_close.svg" />
                </div>

                <div class="pop" v-if="!showLoading">
                    <h2>支払方法登録</h2>
                    <div class="form-row sec_2 last" style="margin-bottom: 50px">
                        <h3>お支払い方法を入力ください</h3>

                        <div class="inner">
                            <p class="sub_t" style="font-size: 18px">カード番号</p>
                            <!-- Stripe処理でElementが追加される -->
                            <div id="card-number" class="w60" style="height: 62px; border: solid 1px #ccc; border-radius: 4px; line-height: 1; padding: 12px;"> </div>
                            <span>※カード番号は16桁。スペース 、ハイフン不要</span>
                            <div v-if="errors.cardNumber.message"><span class="err">{{ errors.cardNumber.message }}</span></div>
                            <br />

                            <p class="sub_t" style="font-size: 18px">名義人</p>
                            <input id="card-name" v-model="cardHolder" class="w60" type="text" placeholder="名義人" />
                            <br />

                            <!-- Stripe処理でElementが追加される -->
                            <p class="sub_t" style="font-size: 18px">セキュリティーコード</p>
                            <div id="card-cvc" class="w30" style="height: 62px; border: solid 1px #ccc; border-radius: 4px; line-height: 1; padding: 12px;"> </div>
                            <span>※〈MasterCard、VISA、JCB、AMEX〉<br>
                                カード裏面の署名欄に印字された末尾3桁の数字です。
                            </span>
                            <div v-if="errors.cardCvc.message"><span class="err">{{ errors.cardCvc.message }}</span></div>
                            <br />

                            <!-- Stripe処理でElementが追加される -->
                            <p class="sub_t" style="font-size: 18px">有効期限</p>
                            <div id="card-expiry" class="w20" style="border: solid 1px #ccc; border-radius: 4px; line-height: 1; padding: 12px;"> </div>
                            <div v-if="errors.cardExpiry.message"><span class="err">{{ errors.cardExpiry.message }}</span></div>
                            <span style="color: red; font-size: 19px">
                                <br>カード会社が提供する 本人認証画面 が表示される場合があります。<br>
                                本人認証画面 が表示された場合には本人認証を完了してください。
                            </span>
                        </div>
                    </div>
                    <div class="bt_box clear">
                        <ul class="modalBtn-area b2">
                            <li>
                                <button type="button" class="bt_clear bt_modal_close" @click="onClickCancel">キャンセル</button>
                            </li>
                            <li>
                                <button class="bt_ok" :disabled="!onCompleted">OK</button>
                            </li>
                        </ul>
                        <p class="bt_modal_close" ref="close" style="display:none"></p>
                    </div>
                </div>
                <div class="pop " v-else>
                    <div class="spinner-container">
                        <div class="spinner">
                            <ImageFrame src="images/index/loading.gif" ></ImageFrame>
                        </div>
                        
                        <div class="message-box">
                            <p><strong>現在決済処理中です</strong></p>
                            <p>ボタンを押したり移動したりせず、このまましばらくお待ちください。処理が完了したら自動的に画面が変わります。</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <a :href="route('payment.end')" ref="paymentEnd" style="display: none"></a>
    </form>
    <!--modal END-->
</template>
<style scoped>
  .spinner-container {
    text-align: center;
  }

  .spinner {
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto 20px;
  }

  .message-box {
    border: 2px solid red;
    padding: 10px;
    color: red;
    max-width: 300px;
    margin: 0 auto;
  }

  .message-box p {
    margin: 5px 0;
  }

  .message-box strong {
    font-weight: bold;
  }
  </style>